import { Client, createClient, fetchExchange } from 'urql';
import {
  offlineExchange,
  cacheExchange as graphExchangeCache,
} from '@urql/exchange-graphcache';
import { retryExchange } from '@urql/exchange-retry';
import { makeDefaultStorage } from '@urql/exchange-graphcache/default-storage';

import { currentWorkspace } from './state/current-workspace';
import { useSnapshot } from 'valtio';
import { CACHE_VERSION } from './state/cache';

import schema from './schema.json';

const clients = new Map<string, { id: string; client: Client }>();

// Value objects without IDs - tell cache to embed them in parent
const keys = {
  Price: () => null,
  PricingStrategy: () => null,
  AgentCommission: () => null,
  InvoiceSummary: () => null,
};

const urqlCacheExchange = (workspaceId: string) =>
  globalThis.window
    ? offlineExchange({
        schema,
        keys,
        storage: makeDefaultStorage({
          idbName: `nivaas-cache-${workspaceId}-v${CACHE_VERSION}`,
          maxAge: 1,
        }),
      })
    : graphExchangeCache({ keys });

export const useClient = (): Client => {
  const { id } = useSnapshot(currentWorkspace);

  if (!clients.has(id)) {
    clients.set(id, {
      id,
      client: createClient({
        url: '/graphql',
        exchanges: [
          urqlCacheExchange(currentWorkspace.id),
          retryExchange({
            initialDelayMs: 1000,
            maxDelayMs: 15000,
            randomDelay: true,
            maxNumberAttempts: 3,
            retryIf: (err) => Boolean(err && err.networkError),
          }),
          fetchExchange,
        ],
        fetchOptions: () => {
          return {
            headers: {
              credentials: 'include',
              'x-workspace-id': currentWorkspace.id,
            },
          };
        },
      }),
    });
  }

  const workspaceClient = clients.get(id);
  if (!workspaceClient) {
    throw new Error('Client not found');
  }

  return workspaceClient.client;
};
